import { recordDraw, recordWin, type EditWinParticipants } from '../../game/record';
import {
  describeEditImpact,
  editDrawTenpai,
  editWinHand,
  prepareDrawTenpaiEdit,
  prepareWinHandEdit,
  type EditImpact,
} from '../../game/editRound';
import { handTiles, logRecognitionResult, takeRecognitionFor } from '../recognitionAccuracy';
import { checkGuestRoundsLabel } from '../shareWatch';
import type { GameStore } from '../../game/store';
import type { Game, RoundRecord } from '../../game/types';
import type { HkosSettings } from '../../rules/hkos';
import { renderBrandHeader } from '../branding';
import type { BonusTile, Tile } from '../../rules/tiles';
import type { DrawContext, HandInput, Meld, PatternHit, WinContext } from '../../rules/types';
import {
  currentTotals,
  deltaSpan,
  esc,
  formatDelta,
  formatHandTiles,
  formatPatternBreakdown,
  formatPhotoThumbnails,
  formatRoundListItem,
  formatRoundOneLine,
  ordinal,
  playerName,
  resolveWinningTile,
  vibrateSuccess,
  windLabel,
} from '../helpers';
import { renderTileGrid, type TileGridContext, type TileGridHandle } from '../tileGrid';
import { mountHandPreview } from '../handPreview';
import { mountHandHelper } from '../handHelper';
import { clearDraftHand, loadDraftHand, saveDraftHand } from '../draftHand';
import { handHelperOn } from '../../game/helper';
import { circumstantialHtml, readCircumstances } from '../winForm';
import { tileGridContextFor } from '../tileGridContext';
import { deriveTableState, seatWindOf } from '../rotation';
import { modeLabel, showHouseRulesPreview } from '../houseRulesPreview';
import { formatSettlementExplanation } from '../settlementExplain';
import { openDiceRoller } from '../diceRoller';
import { canAssignSeating } from '../../game/seating';
import { openHandGuide } from '../handGuide';
import { gameViewerKey, getDefaultWinner, setDefaultWinner } from '../viewerIdentity';
import { updateMarker } from '../updateSafety';

export interface PlayPrefill {
  concealed?: Tile[];
  winningTile?: Tile;
  melds?: Meld[];
  flowers?: BonusTile[];
  photos?: string[];
  /** Tiles the most recent scan wasn't confident about — shown as a "double-check against the photo" callout, since there's no separate review screen for this anymore. */
  needsReviewTiles?: Tile[];
  /** One sentence on what was done when a scan met a hand the player had already entered (see vision/mergeScan.ts). */
  scanNote?: string;
  /** Winner/discarder/self-draw the player had already picked before leaving for Scan Photo — carried back across the round-trip so it isn't silently lost. */
  winner?: string;
  discarder?: string;
  selfDraw?: boolean;
  robbingKong?: boolean;
  kongReplacement?: boolean;
  lastWallTile?: boolean;
  lastDiscard?: boolean;
  heavenlyHand?: boolean;
  earthlyHand?: boolean;
  winDoubleKong?: boolean;
  /** True if a pung or chow was claimed from a discard — see `HandInput.claimedSet`. */
  claimedSet?: boolean;
  /** Tenpai players — only used to fill in a drawn round being corrected. */
  tenpai?: string[];
  /**
   * The round (its `index`) being corrected instead of a fresh one being entered — swaps the form
   * into edit mode: the win/draw toggle is hidden, the button reviews a correction instead of
   * appending a round, and Cancel appears. On an EARLIER round the winner is locked (see
   * `game/editRound.ts` for why); the most recent round may change it too.
   */
  editingRoundIndex?: number;
}

/**
 * What the form currently holds, captured right before navigating away (e.g.
 * to Scan Photo) so it can be restored on return — including whatever tiles
 * were already tapped in, not just who's selected as winner/discarder.
 */
export interface PlayFormState extends Omit<PlayPrefill, 'editingRoundIndex' | 'selfDraw'> {
  winner?: string;
  discarder?: string;
  selfDraw: boolean;
}

export interface PlayActions {
  onEndGame(): void;
  onScanPhoto(current: PlayFormState): void;
  onResumeScan(): void;
  onDiscardScan(): void;
  onEditLastWin(participants: EditWinParticipants): void;
  onEditLastDrawTenpai(tenpai: string[]): void;
  onDeleteLastRound(): void;
  /** Open the Set Seating screen — a "decide with the dice" game, before its first round (or to correct the seating). */
  onSetSeating(): void;
  onShareGame(): void;
  onCheckSubmissions(): void;
  /** How many guest rounds are known to be waiting (0 or omitted: none) — shown on the Check button. */
  guestRoundsWaiting?(): number;
  /** "Guest rounds last checked 14:32" (or that the server can't be reached); empty for a game that isn't shared. */
  guestRoundsNote?(): string;
  /** Says photos are being sent to the guests separately because of a slow connection; empty when they travel with the game as usual. */
  photoSyncNote?(): string;
  /** Fired after any round is recorded or re-scored directly on this device (win, draw, or hand edit) — the hook that keeps a shared guest link's mirrored totals in sync even when no guest submission was involved. */
  onRoundRecorded(): void;
}

export type Message =
  | { kind: 'error'; text: string }
  /** Plain information for the top of the screen (a game started without a connection) — not an error, not a recorded round. */
  | { kind: 'notice'; text: string }
  | { kind: 'success'; text: string; patterns?: PatternHit[]; photos?: string[]; round?: RoundRecord; label?: string }
  | undefined;

export function renderPlay(
  root: HTMLElement,
  game: Game,
  store: GameStore,
  prefill: PlayPrefill | undefined,
  message: Message,
  actions: PlayActions,
  hasPendingScan = false,
): void {
  // A game started with "Decide with the dice" can't record anything until the
  // host says who sits where — the dealer and every seat wind depend on it.
  if (game.seatingPending) {
    renderSeatingPending(root, game, actions, hasPendingScan);
    return;
  }

  const table = deriveTableState(game);
  const totals = currentTotals(game);
  const settings = game.settings as HkosSettings;
  const waitingGuestRounds = actions.guestRoundsWaiting?.() ?? 0;
  const guestNote = actions.guestRoundsNote?.() ?? '';
  const photoNote = actions.photoSyncNote?.() ?? '';

  const scoreboard = game.players
    .map((p, i) => {
      const isDealer = i === table.dealerIndex;
      return `
        <div class="player-tile ${isDealer ? 'is-dealer' : ''}">
          <div class="wind">${windLabel(seatWindOf(i, table.dealerIndex))}${isDealer ? ' · Dealer' : ''}</div>
          <div class="name">${esc(p.name)}</div>
          <div class="score">${deltaSpan(totals[p.id] ?? 0)}</div>
        </div>
      `;
    })
    .join('');

  const playerOptionsSelected = (selected: string | undefined) =>
    game.players
      .map(
        (p) =>
          `<option value="${esc(p.id)}" ${p.id === selected ? 'selected' : ''}>${esc(p.name)}</option>`,
      )
      .join('');

  const lastRound = game.rounds.at(-1);
  const editingRound =
    prefill?.editingRoundIndex !== undefined ? game.rounds.find((r) => r.index === prefill.editingRoundIndex) : undefined;
  const isEditing = !!editingRound;
  /** An earlier round: its winner is locked. */
  const winnerLocked = isEditing && editingRound !== lastRound && editingRound.outcome === 'win';
  // The winner this phone was told is "me" (the tick under the winner field), picked again by default on the next win.
  const idKey = gameViewerKey(game.id);
  const remembered = winnerLocked ? undefined : getDefaultWinner(idKey);
  const rememberedWinner = remembered !== undefined && game.players.some((p) => p.id === remembered) ? remembered : undefined;
  const selectedWinner = prefill?.winner ?? rememberedWinner;
  const lastRoundCorrection =
    lastRound && !game.endedAt && !isEditing
      ? lastRound.outcome === 'win'
        ? `
          <div class="card" id="lastRoundCorrection">
            <h2>Fix Last Round's Players</h2>
            <p class="muted">
              Only who was involved — the fan and points already recorded stay the same.
              ${lastRound.selfDraw ? 'This was a self-draw, so only the winner can be corrected here.' : ''}
            </p>
            <label for="fixWinner">Winner</label>
            <select id="fixWinner">${playerOptionsSelected(lastRound.winner)}</select>
            ${
              lastRound.selfDraw
                ? ''
                : `
                  <label for="fixDiscarder">Discarder</label>
                  <select id="fixDiscarder">${playerOptionsSelected(lastRound.discarder)}</select>
                `
            }
            <button type="button" id="fixLastRoundBtn" style="width:100%;margin-top:0.5rem">Save Correction</button>
            <button type="button" id="editFullHandBtn" class="ghost" style="width:100%;margin-top:0.5rem">
              Edit Full Hand…
            </button>
          </div>
        `
        : `
          <div class="card" id="lastRoundCorrection">
            <h2>Fix Last Round's Players</h2>
            <p class="muted">Who was tenpai on the last (drawn) round.</p>
            ${game.players
              .map(
                (p) => `
                <label class="checkbox-row">
                  <input type="checkbox" class="fix-tenpai-player" value="${esc(p.id)}" ${lastRound.tenpai?.includes(p.id) ? 'checked' : ''} /> ${esc(p.name)}
                </label>
              `,
              )
              .join('')}
            <button type="button" id="fixLastRoundBtn" style="width:100%;margin-top:0.5rem">Save Correction</button>
          </div>
        `
      : '';

  const canDeleteLastRound = !!lastRound && !game.endedAt && !isEditing;

  const circumstantial = circumstantialHtml(settings.toggles, prefill);

  const tenpaiCheckboxes = game.players
    .map(
      (p) => `
      <label class="checkbox-row">
        <input type="checkbox" class="tenpai-player" value="${esc(p.id)}" ${prefill?.tenpai?.includes(p.id) ? 'checked' : ''} /> ${esc(p.name)}
      </label>
    `,
    )
    .join('');

  // Every round can be corrected while the game is live (not while another correction is open).
  const editButtonFor = (r: RoundRecord): string => {
    if (game.endedAt || isEditing) return '';
    if (r.outcome === 'win' && !r.concealed) {
      return `<p class="muted round-edit-note">This round was saved without its tiles, so its hand can't be edited.</p>`;
    }
    return `<button type="button" class="ghost edit-round-btn" data-round="${r.index}">Edit this round…</button>`;
  };
  const roundHistory = [...game.rounds]
    .reverse()
    .map((r) => formatRoundListItem(game, r, editButtonFor(r)))
    .join('');

  root.innerHTML = `
    ${renderBrandHeader()}
    ${editingRound ? '' : updateMarker('ok')}
    ${message?.kind === 'notice' ? `<p class="notice" id="gameNotice" role="status">${esc(message.text)}</p>` : ''}
    ${
      hasPendingScan
        ? `<div class="card" id="pendingScanNotice">
            <p>A photo scan was interrupted before it finished — the photo is still saved.</p>
            <div class="row">
              <button type="button" id="resumeScanBtn">Resume Scan</button>
              <button type="button" id="discardScanBtn" class="secondary">Discard</button>
            </div>
          </div>`
        : ''
    }
    <div class="meta-bar">
      <span>Table ${esc(game.tableId)} <button type="button" id="modeBadge" class="tag">${esc(modeLabel(game, settings))}</button></span>
      <span>Round ${game.rounds.length + 1} · ${windLabel(table.prevailingWind)} round</span>
    </div>

    <div class="row" style="margin-bottom:0.75rem">
      <button type="button" id="shareGameBtn" class="secondary">Share This Game…</button>
      <button type="button" id="checkSubmissionsBtn" class="${waitingGuestRounds > 0 ? '' : 'secondary'}">${checkGuestRoundsLabel(waitingGuestRounds)}</button>
    </div>
    <p id="guestCheckedNote" class="muted live-note" role="status" ${guestNote ? '' : 'hidden'}>${esc(guestNote)}</p>
    <p id="photoSyncNote" class="muted live-note" role="status" ${photoNote ? '' : 'hidden'}>${esc(photoNote)}</p>
    <div class="row ghost-row" style="margin-bottom:0.75rem">
      <button type="button" id="rollDiceMenuBtn" class="ghost">🎲 Roll Dice</button>
      <button type="button" id="handGuideBtn" class="ghost">📖 Winning Hands</button>
    </div>

    <div class="scoreboard">${scoreboard}</div>
    ${
      canAssignSeating(game)
        ? `<button type="button" id="changeSeatingBtn" class="ghost" style="width:100%;margin:0.25rem 0 0.5rem">Change Seating</button>`
        : ''
    }

    <div class="card">
      <h2>${isEditing ? `Edit Round #${editingRound.index}` : 'Record Round'}</h2>
      ${
        isEditing
          ? `<p class="muted">${
              editingRound.outcome === 'draw'
                ? 'Correct who was tenpai. You will see what changes before it is saved.'
                : winnerLocked
                  ? 'Re-scores this round from the corrected hand. The other rounds keep their own results; only the running totals from this round on change. You will see exactly what changes before it is saved.'
                  : 'Re-scores this round from the corrected hand and replaces its recorded fan and points. You will see what changes before it is saved.'
            }</p>`
          : `
            <div class="row">
              <button type="button" id="outcomeWin">Win</button>
              <button type="button" id="outcomeDraw" class="secondary">Draw</button>
            </div>
          `
      }

      <div id="winFields">
        <label for="winner">Winner</label>
        <select id="winner" ${winnerLocked ? 'disabled' : ''}>${playerOptionsSelected(selectedWinner)}</select>
        ${winnerLocked ? '' : `<label class="checkbox-row" id="rememberWinnerRow">
          <input type="checkbox" id="rememberWinner" ${rememberedWinner !== undefined && rememberedWinner === selectedWinner ? 'checked' : ''} /> This is me — remember my name for my next win
        </label>`}
        ${
          winnerLocked
            ? `<p class="muted" id="winnerLockedNote">The winner can't be changed on an earlier round — the deal for every round after it depends on who won. If the winner is wrong, delete back to this round and record it again.</p>`
            : ''
        }

        <label class="checkbox-row">
          <input type="checkbox" id="selfDraw" ${prefill?.selfDraw ? 'checked' : ''} /> Self-draw
        </label>

        <div id="discarderField" style="${prefill?.selfDraw ? 'display:none' : ''}">
          <label for="discarder">Discarder</label>
          <select id="discarder">${playerOptionsSelected(prefill?.discarder)}</select>
        </div>

        <button type="button" id="scanPhotoBtn" class="secondary" style="width:100%;margin:0.5rem 0">
          Scan Photo…
        </button>
        ${
          prefill?.photos?.length
            ? `
              <div id="photoAttachedNote">
                ${prefill.photos.map((p) => `<img class="captured-photo" src="${p}" alt="Photo used for this hand" />`).join('')}
                <p class="muted">Reference photo — <a href="#" id="clearPhotosLink">clear</a></p>
              </div>
            `
            : ''
        }
        ${prefill?.scanNote ? `<p class="muted" id="scanMergeNote" style="margin:0.5rem 0">${esc(prefill.scanNote)}</p>` : ''}
        ${
          prefill?.needsReviewTiles?.length
            ? `<div class="muted" style="margin:0.5rem 0">
                <p style="margin-bottom:0.3rem">Recognition wasn't confident about ${prefill.needsReviewTiles.length === 1 ? 'this tile' : 'these tiles'} — double-check ${prefill.needsReviewTiles.length === 1 ? 'it' : 'them'} against the photo:</p>
                <div class="hand-rack">${prefill.needsReviewTiles.map((t) => `<img class="hand-tile" src="/tile-icons/${t}.png" alt="${esc(t)}" />`).join('')}</div>
              </div>`
            : ''
        }

        <div id="tileGrid"></div>

        ${circumstantial}

        <div id="handPreview" class="hand-preview"></div>
      </div>

      <div id="drawFields" style="display:none">
        <label>Players who were tenpai (ready)</label>
        ${tenpaiCheckboxes}
      </div>

      <div id="formMessage">${message ? renderMessage(message, game) : ''}</div>
      <div id="editPreview"></div>

      <button type="button" id="submitRound" style="width:100%;margin-top:1rem">
        ${isEditing ? 'Review Change' : 'Record Round'}
      </button>
      ${isEditing ? `<button type="button" id="cancelHandEditBtn" class="secondary" style="width:100%;margin-top:0.5rem">Cancel</button>` : ''}
    </div>

    ${lastRoundCorrection}

    <div class="card">
      <div style="display:flex;justify-content:space-between;align-items:center;gap:0.5rem;flex-wrap:wrap">
        <h2 style="margin:0">Rounds (${game.rounds.length})</h2>
        ${canDeleteLastRound ? `<button type="button" id="deleteLastRoundBtn" style="flex-shrink:0">Delete Last Round</button>` : ''}
      </div>
      <div id="deleteLastRoundConfirm"></div>
      <ul class="round-list">${roundHistory || '<li class="muted">No rounds recorded yet.</li>'}</ul>
    </div>

    <div id="endGameSection">
      <button type="button" id="endGameBtn" class="danger" style="width:100%">End Game</button>
    </div>
  `;

  if (message?.kind === 'success') vibrateSuccess();

  wireUp(root, game, store, table, prefill, actions);
}

/**
 * The play screen for a game whose seating hasn't been set yet ("Decide with
 * the dice"). The host can still share the link, roll the dice and browse
 * the winning hands; the round form is replaced by a single "Set Seating"
 * step. Guests are held at the same point by the shared state's
 * `seatingPending` flag (see guestPlay.ts).
 */
function renderSeatingPending(
  root: HTMLElement,
  game: Game,
  actions: PlayActions,
  hasPendingScan: boolean,
): void {
  const settings = game.settings as HkosSettings;
  const totals = currentTotals(game);
  const scoreboard = game.players
    .map(
      (p) => `
        <div class="player-tile">
          <div class="wind">Seat not set</div>
          <div class="name">${esc(p.name)}</div>
          <div class="score">${deltaSpan(totals[p.id] ?? 0)}</div>
        </div>`,
    )
    .join('');

  root.innerHTML = `
    ${renderBrandHeader()}
    ${updateMarker('ok')}
    ${
      hasPendingScan
        ? `<div class="card"><p>A photo scan was interrupted earlier — it can be resumed once the seating is set and rounds are being recorded.</p></div>`
        : ''
    }
    <div class="meta-bar">
      <span>Table ${esc(game.tableId)} <button type="button" id="modeBadge" class="tag">${esc(modeLabel(game, settings))}</button></span>
      <span>Seating not set yet</span>
    </div>

    <button type="button" id="shareGameBtn" class="secondary" style="width:100%;margin-bottom:0.75rem">Share This Game…</button>
    <div class="row ghost-row" style="margin-bottom:0.75rem">
      <button type="button" id="rollDiceMenuBtn" class="ghost">🎲 Roll Dice</button>
      <button type="button" id="handGuideBtn" class="ghost">📖 Winning Hands</button>
    </div>

    <div class="scoreboard">${scoreboard}</div>

    <div class="card" id="seatingPendingCard">
      <h2>Decide the seating</h2>
      <p class="muted">
        Share the game link with the guests, then everyone rolls the dice to see who sits where.
        When you know, tap Set Seating — rounds can be recorded from then on.
      </p>
      <button type="button" id="setSeatingBtn" style="width:100%">Set Seating</button>
    </div>

    <div id="endGameSection">
      <button type="button" id="endGameBtn" class="danger" style="width:100%">End Game</button>
    </div>
  `;

  root.querySelector<HTMLButtonElement>('#modeBadge')!.addEventListener('click', () => {
    showHouseRulesPreview(settings, game.leaderboard ? 'Leaderboard rules' : undefined);
  });
  root.querySelector<HTMLButtonElement>('#shareGameBtn')!.addEventListener('click', () => actions.onShareGame());
  root.querySelector<HTMLButtonElement>('#rollDiceMenuBtn')!.addEventListener('click', () => openDiceRoller());
  root.querySelector<HTMLButtonElement>('#handGuideBtn')!.addEventListener('click', () => openHandGuide(settings));
  root.querySelector<HTMLButtonElement>('#setSeatingBtn')!.addEventListener('click', () => actions.onSetSeating());
  wireEndGame(root, actions, !!game.leaderboard);
}

function renderMessage(message: NonNullable<Message>, game: Game): string {
  if (message.kind === 'notice') return ''; // shown at the top of the screen instead
  if (message.kind === 'error') {
    return `<div class="error-box">${esc(message.text)}</div>`;
  }
  // Sits directly above the (now-reset) entry form, right where a player's
  // eye lands next — without a label it reads as if it's describing the
  // round about to be entered, not confirming the one just recorded.
  //
  // Includes the same illustrated tile rack Round History shows below its
  // photo — a legible cross-check, not just a fallback, per formatHandTiles'
  // own rationale. This box is the first place anyone actually looks right
  // after recording a round, so it shouldn't be the one place missing it.
  const breakdown =
    formatPatternBreakdown(message.patterns ?? []) +
    (message.round ? formatSettlementExplanation(game, message.round) : '') +
    formatPhotoThumbnails(message.photos) +
    (message.round ? formatHandTiles(message.round) : '');
  return `
    <div class="result-box">
      <span class="result-box-label">${esc(message.label ?? 'Last round recorded')}</span>
      ${esc(message.text)}${breakdown}
    </div>
  `;
}

function wireUp(
  root: HTMLElement,
  game: Game,
  store: GameStore,
  table: ReturnType<typeof deriveTableState>,
  prefill: PlayPrefill | undefined,
  actions: PlayActions,
): void {
  const winBtn = root.querySelector<HTMLButtonElement>('#outcomeWin');
  const drawBtn = root.querySelector<HTMLButtonElement>('#outcomeDraw');
  const winFields = root.querySelector<HTMLDivElement>('#winFields')!;
  const drawFields = root.querySelector<HTMLDivElement>('#drawFields')!;
  let outcome: 'win' | 'draw' = 'win';
  let preview: { refresh(): void } | undefined; // the live preview under the tiles; set once the tile grid exists
  let helper: { refresh(): void } | undefined; // the Hand helper's Suggestions under "Your hand"; likewise

  const setOutcome = (o: 'win' | 'draw') => {
    outcome = o;
    preview?.refresh();
    if (winBtn) winBtn.className = o === 'win' ? '' : 'secondary';
    if (drawBtn) drawBtn.className = o === 'draw' ? '' : 'secondary';
    winFields.style.display = o === 'win' ? '' : 'none';
    drawFields.style.display = o === 'draw' ? '' : 'none';
  };
  winBtn?.addEventListener('click', () => setOutcome('win'));
  drawBtn?.addEventListener('click', () => setOutcome('draw'));
  const editingRound =
    prefill?.editingRoundIndex !== undefined ? game.rounds.find((r) => r.index === prefill.editingRoundIndex) : undefined;
  if (editingRound?.outcome === 'draw') setOutcome('draw');

  const selfDrawCb = root.querySelector<HTMLInputElement>('#selfDraw')!;
  const discarderField = root.querySelector<HTMLDivElement>('#discarderField')!;
  selfDrawCb.addEventListener('change', () => {
    discarderField.style.display = selfDrawCb.checked ? 'none' : '';
  });

  // Badges on the tile grid ("R"/"S" on a wind or flower/season tile) need
  // the round's prevailing wind (fixed for this round) and the currently
  // selected winner's seat wind (follows the #winner select live). When
  // editing a past round's hand, dealer/prevailing wind come from that
  // round's own stored values, not the live table state — see
  // game/editRound.ts for why those can't be re-derived here.
  const settings = game.settings as HkosSettings;
  const roundPrevailingWind = editingRound?.prevailingWind ?? table.prevailingWind;
  const dealerId = editingRound?.dealer ?? game.players[table.dealerIndex]!.id;
  const dealerIndex = game.players.findIndex((p) => p.id === dealerId);

  function tileGridContext(): TileGridContext {
    return tileGridContextFor(
      settings,
      game.players.map((p) => p.id),
      dealerIndex,
      roundPrevailingWind,
      root.querySelector<HTMLSelectElement>('#winner')?.value,
    );
  }

  // A hand left half-entered by a reload comes back (only on a fresh form: not a correction, not a photo scan's result).
  const draftKey = gameViewerKey(game.id);
  const draft = !prefill && !editingRound ? loadDraftHand(draftKey) : undefined;

  const tileGrid = renderTileGrid(
    root.querySelector<HTMLDivElement>('#tileGrid')!,
    {
      concealed: prefill?.concealed ?? draft?.concealed ?? [],
      melds: prefill?.melds ?? draft?.melds ?? [],
      winningTile: prefill?.winningTile ?? draft?.winningTile,
      flowers: prefill?.flowers ?? draft?.flowers ?? [],
      claimedSet: prefill?.claimedSet ?? draft?.claimedSet,
    },
    tileGridContext(),
    {
      onChange: () => {
        preview?.refresh();
        helper?.refresh();
        if (!editingRound) saveDraftHand(draftKey, tileGrid.read()); // the hand being built survives a reload
      },
    },
  );
  root.querySelector<HTMLSelectElement>('#winner')!.addEventListener('change', () => {
    tileGrid.updateContext(tileGridContext());
  });

  // The preview reads the form exactly as Record Round does (same winner / seat / dealer / extras), and recalculates whenever
  // anything in it changes: a tile (the grid calls back), or a select / tick (this one listener).
  preview = mountHandPreview(root.querySelector<HTMLDivElement>('#handPreview')!, game, {
    read: () => {
      if (outcome !== 'win') return undefined;
      const winnerId = root.querySelector<HTMLSelectElement>('#winner')!.value;
      const selfDraw = selfDrawCb.checked;
      const discarderId = root.querySelector<HTMLSelectElement>('#discarder')!.value;
      if (!selfDraw && discarderId === winnerId) return { blocked: 'Choose who discarded — it can\'t be the winner — to see the preview.' };
      const { concealed, melds, winningTile: picked, flowers, claimedSet } = tileGrid.read();
      const circ = readCircumstances(root);
      return {
        hand: { concealed, melds, winningTile: resolveWinningTile(picked, concealed, circ.heavenlyHand), flowers, claimedSet },
        ctx: {
          winner: winnerId,
          seatWind: seatWindOf(Math.max(0, game.players.findIndex((p) => p.id === winnerId)), dealerIndex),
          prevailingWind: roundPrevailingWind,
          dealer: dealerId,
          selfDraw,
          ...(selfDraw ? {} : { discarder: discarderId }),
          ...circ,
        },
      };
    },
  });
  winFields.addEventListener('change', () => preview?.refresh());

  // Suggestions (the Hand helper): only while the House Rules switch is on, never in a leaderboard game, never while correcting a round.
  const helperSlot = root.querySelector<HTMLDivElement>('#tgHelper');
  if (helperSlot && !editingRound && handHelperOn(game)) {
    helper = mountHandHelper(helperSlot, game, {
      read: () => {
        const { concealed, melds } = tileGrid.read();
        return { concealed, kongs: melds.filter((m) => m.type === 'kong').map((m) => ({ tile: m.tiles[0]!, concealed: m.concealed })) };
      },
    });
  }

  // "This is me": ticking it makes the chosen winner this phone's default, so the next win needs no picking; choosing a different name
  // un-ticks it (a phone that records someone else's win must not change whose phone it is), and unticking forgets the default.
  const winnerSelect = root.querySelector<HTMLSelectElement>('#winner');
  const rememberBox = root.querySelector<HTMLInputElement>('#rememberWinner');
  const key = gameViewerKey(game.id);
  if (winnerSelect && rememberBox && key) {
    winnerSelect.addEventListener('change', () => {
      rememberBox.checked = getDefaultWinner(key) === winnerSelect.value;
    });
    rememberBox.addEventListener('change', () => setDefaultWinner(key, rememberBox.checked ? winnerSelect.value : undefined));
  }

  root.querySelector<HTMLButtonElement>('#modeBadge')!.addEventListener('click', () => {
    showHouseRulesPreview(settings, game.leaderboard ? 'Leaderboard rules' : undefined);
  });

  root.querySelector<HTMLButtonElement>('#shareGameBtn')!.addEventListener('click', () => {
    actions.onShareGame();
  });
  root.querySelector<HTMLButtonElement>('#checkSubmissionsBtn')!.addEventListener('click', () => {
    actions.onCheckSubmissions();
  });
  root.querySelector<HTMLButtonElement>('#rollDiceMenuBtn')!.addEventListener('click', () => {
    openDiceRoller();
  });
  root.querySelector<HTMLButtonElement>('#handGuideBtn')!.addEventListener('click', () => {
    openHandGuide(settings);
  });
  root.querySelector<HTMLButtonElement>('#changeSeatingBtn')?.addEventListener('click', () => {
    actions.onSetSeating();
  });

  let photos: string[] = prefill?.photos ?? [];
  root.querySelector<HTMLButtonElement>('#scanPhotoBtn')!.addEventListener('click', () => {
    const { concealed, melds, winningTile, flowers, claimedSet } = tileGrid.read();
    actions.onScanPhoto({
      winner: root.querySelector<HTMLSelectElement>('#winner')!.value,
      discarder: selfDrawCb.checked
        ? undefined
        : root.querySelector<HTMLSelectElement>('#discarder')!.value,
      selfDraw: selfDrawCb.checked,
      concealed,
      melds,
      winningTile,
      flowers,
      claimedSet,
      photos,
      robbingKong: root.querySelector<HTMLInputElement>('#robbingKong')?.checked,
      kongReplacement: root.querySelector<HTMLInputElement>('#kongReplacement')?.checked,
      lastWallTile: root.querySelector<HTMLInputElement>('#lastWallTile')?.checked,
      lastDiscard: root.querySelector<HTMLInputElement>('#lastDiscard')?.checked,
      heavenlyHand: root.querySelector<HTMLInputElement>('#heavenlyHand')?.checked,
      earthlyHand: root.querySelector<HTMLInputElement>('#earthlyHand')?.checked,
      winDoubleKong: root.querySelector<HTMLInputElement>('#winDoubleKong')?.checked,
    });
  });
  root.querySelector<HTMLButtonElement>('#resumeScanBtn')?.addEventListener('click', () => {
    actions.onResumeScan();
  });
  root.querySelector<HTMLButtonElement>('#discardScanBtn')?.addEventListener('click', () => {
    actions.onDiscardScan();
  });
  root.querySelector<HTMLAnchorElement>('#clearPhotosLink')?.addEventListener('click', (e) => {
    e.preventDefault();
    photos = [];
    root.querySelector('#photoAttachedNote')?.remove();
  });

  const formMessage = root.querySelector<HTMLDivElement>('#formMessage')!;
  const showError = (text: string) => {
    formMessage.innerHTML = renderMessage({ kind: 'error', text }, game);
  };

  root.querySelector<HTMLButtonElement>('#fixLastRoundBtn')?.addEventListener('click', () => {
    const last = game.rounds.at(-1);
    if (!last) return;
    if (last.outcome === 'win') {
      const winner = root.querySelector<HTMLSelectElement>('#fixWinner')!.value;
      const discarderSelect = root.querySelector<HTMLSelectElement>('#fixDiscarder');
      const discarder = discarderSelect?.value;
      if (discarderSelect && discarder === winner) {
        showError('The discarder must be a different player from the winner.');
        return;
      }
      actions.onEditLastWin({ winner, ...(discarder ? { discarder } : {}) });
    } else {
      const tenpai = [...root.querySelectorAll<HTMLInputElement>('.fix-tenpai-player:checked')].map(
        (el) => el.value,
      );
      actions.onEditLastDrawTenpai(tenpai);
    }
  });

  const deleteLastRoundBtn = root.querySelector<HTMLButtonElement>('#deleteLastRoundBtn');
  const deleteLastRoundConfirm = root.querySelector<HTMLDivElement>('#deleteLastRoundConfirm');
  deleteLastRoundBtn?.addEventListener('click', () => {
    const last = game.rounds.at(-1);
    if (!last || !deleteLastRoundConfirm) return;
    deleteLastRoundBtn.disabled = true;
    deleteLastRoundConfirm.innerHTML = `
      <div class="card" style="margin-top:0.5rem">
        <p><strong>Delete round #${last.index} — ${esc(formatRoundOneLine(game, last))}?</strong></p>
        <p class="muted">
          This removes it entirely — use this only for an accidental duplicate submission, not to fix a
          scoring mistake (use "Edit Full Hand…" above for that instead). This can't be undone.
        </p>
        <div class="row">
          <button type="button" id="deleteLastRoundCancelBtn" class="secondary">Cancel</button>
          <button type="button" id="deleteLastRoundConfirmBtn" class="danger">Yes, Delete</button>
        </div>
      </div>
    `;
    deleteLastRoundConfirm.querySelector<HTMLButtonElement>('#deleteLastRoundCancelBtn')!.addEventListener(
      'click',
      () => {
        deleteLastRoundConfirm.innerHTML = '';
        deleteLastRoundBtn.disabled = false;
      },
    );
    deleteLastRoundConfirm.querySelector<HTMLButtonElement>('#deleteLastRoundConfirmBtn')!.addEventListener(
      'click',
      () => {
        actions.onDeleteLastRound();
      },
    );
  });

  // Open the correction form for a round — from the round list's button, or the last round's card.
  const openEdit = (round: RoundRecord): void => {
    renderPlay(root, game, store, prefillForRound(round), undefined, actions);
    window.scrollTo(0, 0);
  };
  root.querySelector<HTMLButtonElement>('#editFullHandBtn')?.addEventListener('click', () => {
    const last = game.rounds.at(-1);
    if (last?.outcome === 'win') openEdit(last);
  });
  root.querySelectorAll<HTMLButtonElement>('.edit-round-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const round = game.rounds.find((r) => r.index === Number(btn.dataset['round']));
      if (round) openEdit(round);
    });
  });

  root.querySelector<HTMLButtonElement>('#cancelHandEditBtn')?.addEventListener('click', () => {
    renderPlay(root, game, store, undefined, undefined, actions);
  });

  root.querySelector<HTMLButtonElement>('#submitRound')!.addEventListener('click', async () => {
    if (editingRound?.outcome === 'win') {
      reviewWinEdit(root, game, store, editingRound.index, tileGrid, () => photos, showError, actions);
    } else if (editingRound?.outcome === 'draw') {
      reviewDrawEdit(root, game, store, editingRound.index, showError, actions);
    } else if (outcome === 'win') {
      await submitWin(root, game, store, table, tileGrid, () => photos, showError, actions);
    } else {
      await submitDraw(root, game, store, table, showError, actions);
    }
  });

  wireEndGame(root, actions, !!game.leaderboard);
}

/** The End Game button and its "Yes, End Game" confirmation — the same on every play view. */
function wireEndGame(root: HTMLElement, actions: PlayActions, leaderboard: boolean): void {
  const endGameSection = root.querySelector<HTMLDivElement>('#endGameSection')!;
  const wireEndGameButton = () => {
    endGameSection.querySelector<HTMLButtonElement>('#endGameBtn')!.addEventListener('click', () => {
      endGameSection.innerHTML = `
        <div class="card">
          <p><strong>End the game?</strong></p>
          <p class="muted">
            After this, you'll need to start a new game from the beginning to keep playing — the rounds
            already recorded stay saved either way.
          </p>
          ${
            leaderboard
              ? '<p class="muted">This is a leaderboard game: ending it sends the final scores to the leaderboard.</p>'
              : ''
          }
          <div class="row">
            <button type="button" id="endGameCancelBtn" class="secondary">Cancel</button>
            <button type="button" id="endGameConfirmBtn" class="danger">Yes, End Game</button>
          </div>
        </div>
      `;
      endGameSection.querySelector<HTMLButtonElement>('#endGameCancelBtn')!.addEventListener('click', () => {
        endGameSection.innerHTML = `<button type="button" id="endGameBtn" class="danger" style="width:100%">End Game</button>`;
        wireEndGameButton();
      });
      endGameSection.querySelector<HTMLButtonElement>('#endGameConfirmBtn')!.addEventListener('click', () => {
        actions.onEndGame();
      });
    });
  };
  wireEndGameButton();
}

async function submitWin(
  root: HTMLElement,
  game: Game,
  store: GameStore,
  table: ReturnType<typeof deriveTableState>,
  tileGrid: TileGridHandle,
  getPhotos: () => string[],
  showError: (text: string) => void,
  actions: PlayActions,
): Promise<void> {
  const winnerId = root.querySelector<HTMLSelectElement>('#winner')!.value;
  const selfDraw = root.querySelector<HTMLInputElement>('#selfDraw')!.checked;
  const discarderId = root.querySelector<HTMLSelectElement>('#discarder')!.value;

  if (!selfDraw && discarderId === winnerId) {
    showError('The discarder must be a different player from the winner.');
    return;
  }

  if (tileGrid.hasUnsavedClaim()) {
    showError('You have an unfinished kong — tap "Add Kong" to include it, or "Cancel" to discard it.');
    return;
  }

  const { concealed, melds, winningTile: pickedWinningTile, flowers, claimedSet } = tileGrid.read();
  const circ = readCircumstances(root);
  const winningTile = resolveWinningTile(pickedWinningTile, concealed, circ.heavenlyHand);
  if (!winningTile) {
    showError('Select the winning tile.');
    return;
  }

  const hand: HandInput = {
    concealed,
    melds,
    winningTile,
    flowers,
    claimedSet,
  };

  const winnerIndex = game.players.findIndex((p) => p.id === winnerId);
  const ctx: WinContext = {
    winner: winnerId,
    seatWind: seatWindOf(winnerIndex, table.dealerIndex),
    prevailingWind: table.prevailingWind,
    dealer: game.players[table.dealerIndex]!.id,
    selfDraw,
    ...(selfDraw ? {} : { discarder: discarderId }),
    ...circ,
  };

  const { round, problems } = recordWin(game, hand, ctx, getPhotos());
  if (problems.length > 0) {
    showError(problems.join('\n'));
    return;
  }

  await store.save(game);
  clearDraftHand(gameViewerKey(game.id)); // the round is in: the next one starts with an empty hand
  actions.onRoundRecorded();
  // If this hand came from a photo scan, compare what the scan proposed with the hand as saved.
  const scan = takeRecognitionFor(getPhotos());
  if (scan) logRecognitionResult(scan, handTiles(hand));

  const deltas = round.payments
    .filter((p) => p.delta !== 0)
    .map((p) => `${playerName(game, p.player)} ${formatDelta(p.delta)}`)
    .join(', ');
  const successText = `${playerName(game, round.winner)} won — ${round.effectiveFan} fan${round.isLimit ? ' (limit)' : ''}. ${deltas}`;

  renderPlay(
    root,
    game,
    store,
    undefined,
    { kind: 'success', text: successText, patterns: round.patterns, photos: round.photos, round },
    actions,
  );
}

/**
 * The form values for a correction, or the problem that stops it. Dealer / round wind / seat wind
 * are NOT taken from the table (which reflects the state after this round): the engine uses the
 * round's own stored values — see `game/editRound.ts`.
 */
function readWinEditForm(
  root: HTMLElement,
  game: Game,
  tileGrid: TileGridHandle,
  roundIndex: number,
): { hand: HandInput; ctx: WinContext } | { error: string } {
  const round = game.rounds.find((r) => r.index === roundIndex);
  if (!round || round.outcome !== 'win') return { error: 'That round is not a recorded win.' };

  const winnerId = root.querySelector<HTMLSelectElement>('#winner')!.value;
  const selfDraw = root.querySelector<HTMLInputElement>('#selfDraw')!.checked;
  const discarderId = root.querySelector<HTMLSelectElement>('#discarder')!.value;

  if (!selfDraw && discarderId === winnerId) return { error: 'The discarder must be a different player from the winner.' };
  if (tileGrid.hasUnsavedClaim()) {
    return { error: 'You have an unfinished kong — tap "Add Kong" to include it, or "Cancel" to discard it.' };
  }

  const { concealed, melds, winningTile: pickedWinningTile, flowers, claimedSet } = tileGrid.read();
  const heavenlyHand = root.querySelector<HTMLInputElement>('#heavenlyHand')?.checked ?? false;
  const winningTile = resolveWinningTile(pickedWinningTile, concealed, heavenlyHand);
  if (!winningTile) return { error: 'Select the winning tile.' };

  const hand: HandInput = { concealed, melds, winningTile, flowers, claimedSet };
  // dealer / prevailingWind / seatWind here are placeholders: the engine uses the round's own stored values.
  const ctx: WinContext = {
    winner: winnerId,
    seatWind: 'east',
    prevailingWind: round.prevailingWind,
    dealer: round.dealer,
    selfDraw,
    ...(selfDraw ? {} : { discarder: discarderId }),
    robbingKong: root.querySelector<HTMLInputElement>('#robbingKong')?.checked ?? false,
    kongReplacement: root.querySelector<HTMLInputElement>('#kongReplacement')?.checked ?? false,
    lastWallTile: root.querySelector<HTMLInputElement>('#lastWallTile')?.checked ?? false,
    lastDiscard: root.querySelector<HTMLInputElement>('#lastDiscard')?.checked ?? false,
    heavenlyHand,
    earthlyHand: root.querySelector<HTMLInputElement>('#earthlyHand')?.checked ?? false,
    winDoubleKong: root.querySelector<HTMLInputElement>('#winDoubleKong')?.checked ?? false,
  };
  return { hand, ctx };
}

/**
 * Step 1 of correcting a win: re-score it WITHOUT saving, and show what would change (the round's
 * fan and payments, everyone's totals, any change to the ranking) so a wrong tap can't quietly
 * reshuffle the standings. Nothing is written until "Confirm and Save".
 */
function reviewWinEdit(
  root: HTMLElement,
  game: Game,
  store: GameStore,
  roundIndex: number,
  tileGrid: TileGridHandle,
  getPhotos: () => string[],
  showError: (text: string) => void,
  actions: PlayActions,
): void {
  const form = readWinEditForm(root, game, tileGrid, roundIndex);
  if ('error' in form) return showError(form.error);

  const prepared = prepareWinHandEdit(game, roundIndex, form.hand, form.ctx, getPhotos());
  if (prepared.problems.length > 0) return showError(prepared.problems.join('\n'));
  if (prepared.unchanged) return showEditUnchanged(root, roundIndex);

  showEditPreview(root, game, describeEditImpact(game, prepared.round), async () => {
    const applied = editWinHand(game, roundIndex, form.hand, form.ctx, getPhotos());
    if (applied.problems.length > 0) return showError(applied.problems.join('\n'));
    await finishEdit(root, game, store, applied.round, actions);
  });
}

/** Step 1 of correcting a draw's tenpai list — same review-then-confirm as a win. */
function reviewDrawEdit(
  root: HTMLElement,
  game: Game,
  store: GameStore,
  roundIndex: number,
  showError: (text: string) => void,
  actions: PlayActions,
): void {
  const tenpai = [...root.querySelectorAll<HTMLInputElement>('.tenpai-player:checked')].map((el) => el.value);
  const prepared = prepareDrawTenpaiEdit(game, roundIndex, tenpai);
  if (prepared.problems.length > 0) return showError(prepared.problems.join('\n'));
  if (prepared.unchanged) return showEditUnchanged(root, roundIndex);

  showEditPreview(root, game, describeEditImpact(game, prepared.round), async () => {
    const applied = editDrawTenpai(game, roundIndex, tenpai);
    if (applied.problems.length > 0) return showError(applied.problems.join('\n'));
    await finishEdit(root, game, store, applied.round, actions);
  });
}

function showEditUnchanged(root: HTMLElement, roundIndex: number): void {
  root.querySelector<HTMLDivElement>('#editPreview')!.innerHTML = `
    <div class="card"><p><strong>Nothing changed.</strong> Round #${roundIndex} is already exactly like this, so there is nothing to save.</p></div>
  `;
}

/** The "check this correction" card: what changes, and Confirm / Back. Writes nothing itself. */
function showEditPreview(root: HTMLElement, game: Game, impact: EditImpact, onConfirm: () => Promise<void>): void {
  const box = root.querySelector<HTMLDivElement>('#editPreview')!;
  const changed = (a: number, b: number, fmt: (n: number) => string): string =>
    a === b ? fmt(b) : `${fmt(a)} → <strong>${fmt(b)}</strong>`;

  const rows = impact.players
    .map((p) => {
      const rank = p.rankBefore === p.rankAfter ? '' : ` <span class="rank-move">${ordinal(p.rankBefore)} → ${ordinal(p.rankAfter)}</span>`;
      return `
        <tr>
          <td>${esc(playerName(game, p.player))}</td>
          <td>${changed(p.roundBefore, p.roundAfter, formatDelta)}</td>
          <td>${changed(p.totalBefore, p.totalAfter, formatDelta)}${rank}</td>
        </tr>`;
    })
    .join('');

  const movers = impact.players
    .filter((p) => p.rankBefore !== p.rankAfter)
    .map((p) => `${esc(playerName(game, p.player))} ${ordinal(p.rankBefore)} → ${ordinal(p.rankAfter)}`)
    .join(', ');
  const fan =
    impact.fanBefore !== undefined && impact.fanAfter !== undefined
      ? `<p>Fan: ${impact.fanBefore === impact.fanAfter ? `stays <strong>${impact.fanAfter}</strong>` : `${impact.fanBefore} → <strong>${impact.fanAfter}</strong>`}</p>`
      : '';

  box.innerHTML = `
    <div class="card edit-preview">
      <h3>Check this correction — Round #${impact.roundIndex}</h3>
      ${fan}
      <table class="summary">
        <thead><tr><th>Player</th><th>This round</th><th>Total score</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <p>${impact.rankingChanged ? `<strong>The ranking changes:</strong> ${movers}.` : 'The ranking stays the same.'}</p>
      ${
        impact.laterRounds > 0
          ? `<p class="muted">The ${impact.laterRounds} round${impact.laterRounds === 1 ? '' : 's'} after this one keep their own results; only the running totals change.</p>`
          : ''
      }
      <p class="muted">The round will be marked <strong>Edited</strong>, and the original result kept.</p>
      <div class="row">
        <button type="button" id="editBackBtn" class="secondary">Back to editing</button>
        <button type="button" id="editConfirmBtn">Confirm and Save</button>
      </div>
    </div>
  `;
  box.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  box.querySelector<HTMLButtonElement>('#editBackBtn')!.addEventListener('click', () => {
    box.innerHTML = '';
  });
  const confirm = box.querySelector<HTMLButtonElement>('#editConfirmBtn')!;
  confirm.addEventListener('click', () => {
    confirm.disabled = true; // one tap only: a second could apply the same correction twice
    void onConfirm().finally(() => {
      confirm.disabled = false;
    });
  });
}

/** Saves a confirmed correction, tells the guests' shared copy, and returns to the round-entry screen. */
async function finishEdit(
  root: HTMLElement,
  game: Game,
  store: GameStore,
  round: RoundRecord,
  actions: PlayActions,
): Promise<void> {
  await store.save(game);
  actions.onRoundRecorded();

  const deltas = round.payments
    .filter((p) => p.delta !== 0)
    .map((p) => `${playerName(game, p.player)} ${formatDelta(p.delta)}`)
    .join(', ');
  const summary =
    round.outcome === 'win'
      ? `${playerName(game, round.winner)} won, ${round.effectiveFan} fan${round.isLimit ? ' (limit)' : ''}. ${deltas}`
      : `Draw.${deltas ? ` ${deltas}` : ''}`;

  renderPlay(
    root,
    game,
    store,
    undefined,
    {
      kind: 'success',
      label: `Round #${round.index} corrected`,
      text: `${summary} Totals are updated from this round on.`,
      patterns: round.patterns,
      photos: round.photos,
      round,
    },
    actions,
  );
}

/** What the form should hold when opening a round for correction. */
function prefillForRound(r: RoundRecord): PlayPrefill {
  if (r.outcome === 'draw') return { editingRoundIndex: r.index, tenpai: r.tenpai ?? [] };
  return {
    concealed: r.concealed,
    melds: r.melds,
    winningTile: r.winningTile,
    flowers: r.flowers,
    photos: r.photos,
    winner: r.winner,
    discarder: r.discarder,
    selfDraw: r.selfDraw,
    robbingKong: r.patterns?.some((p) => p.key === 'robbingKong') ?? false,
    kongReplacement: r.patterns?.some((p) => p.key === 'kongReplacement') ?? false,
    lastWallTile: r.patterns?.some((p) => p.key === 'lastWallTile') ?? false,
    lastDiscard: r.patterns?.some((p) => p.key === 'lastDiscard') ?? false,
    heavenlyHand: r.patterns?.some((p) => p.key === 'heavenlyHand') ?? false,
    earthlyHand: r.patterns?.some((p) => p.key === 'earthlyHand') ?? false,
    winDoubleKong: r.patterns?.some((p) => p.key === 'winDoubleKong') ?? false,
    claimedSet: r.claimedSet ?? false,
    editingRoundIndex: r.index,
  };
}

async function submitDraw(
  root: HTMLElement,
  game: Game,
  store: GameStore,
  table: ReturnType<typeof deriveTableState>,
  _showError: (text: string) => void,
  actions: PlayActions,
): Promise<void> {
  const tenpai = [...root.querySelectorAll<HTMLInputElement>('.tenpai-player:checked')].map(
    (el) => el.value,
  );
  const ctx: DrawContext = { dealer: game.players[table.dealerIndex]!.id, tenpai };
  recordDraw(game, ctx);
  await store.save(game);
  actions.onRoundRecorded();
  renderPlay(root, game, store, undefined, { kind: 'success', text: 'Draw recorded.' }, actions);
}
